import React, { type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import styles from "./MarkdownEditor.module.css";

interface MarkdownPreviewProps {
  content: string;
}

/**
 * Markdown preview using react-markdown with rehype-sanitize for XSS protection.
 * Replaces the previous regex + dangerouslySetInnerHTML approach.
 * Fixes #212.
 */
export function MarkdownPreview({ content }: MarkdownPreviewProps) {
  if (!content) {
    return (
      <div className={styles.preview}>
        <p className={styles.empty}>Nothing to preview</p>
      </div>
    );
  }

  return (
    <div className={styles.preview}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeSanitize]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/** Walk children and highlight podcast timestamps like [1:23] or [01:23:45]. */
function processChildren(children: ReactNode): ReactNode {
  if (typeof children === "string") {
    return highlightTimestamps(children);
  }
  if (Array.isArray(children)) {
    return children.map((child, i) =>
      typeof child === "string" ? <span key={i}>{highlightTimestamps(child)}</span> : child,
    );
  }
  return children;
}

const TIMESTAMP_RE = /\[(\d{1,2}:\d{2}(?::\d{2})?)\]/g;

function highlightTimestamps(text: string): ReactNode {
  const parts: (string | React.JSX.Element)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = TIMESTAMP_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span key={match.index} className={styles.timestamp}>
        [{match[1]}]
      </span>,
    );
    lastIndex = TIMESTAMP_RE.lastIndex;
  }

  if (lastIndex === 0) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

/** Custom components that inject timestamp highlighting and safe link behavior. */
const components: Components = {
  p: ({ children, node: _node, ...props }) => <p {...props}>{processChildren(children)}</p>,
  li: ({ children, node: _node, ...props }) => <li {...props}>{processChildren(children)}</li>,
  td: ({ children, node: _node, ...props }) => <td {...props}>{processChildren(children)}</td>,
  h1: ({ children, node: _node, ...props }) => <h1 {...props}>{processChildren(children)}</h1>,
  h2: ({ children, node: _node, ...props }) => <h2 {...props}>{processChildren(children)}</h2>,
  h3: ({ children, node: _node, ...props }) => <h3 {...props}>{processChildren(children)}</h3>,
  h4: ({ children, node: _node, ...props }) => <h4 {...props}>{processChildren(children)}</h4>,
  h5: ({ children, node: _node, ...props }) => <h5 {...props}>{processChildren(children)}</h5>,
  h6: ({ children, node: _node, ...props }) => <h6 {...props}>{processChildren(children)}</h6>,
  blockquote: ({ children, node: _node, ...props }) => (
    <blockquote {...props}>{processChildren(children)}</blockquote>
  ),
  strong: ({ children, node: _node, ...props }) => (
    <strong {...props}>{processChildren(children)}</strong>
  ),
  em: ({ children, node: _node, ...props }) => <em {...props}>{processChildren(children)}</em>,
  a: ({ href, children, node: _node, ...rest }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {processChildren(children)}
    </a>
  ),
};
